import { useCallback, useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiService } from 'api/services/ApiService';
import {
  AttioQueryKeys,
  CREATED_AT_KEY,
  DEAL_NAME_KEY,
  DESC,
  NOTES_KEY,
  STATUS_KEY,
  VALUATION_DATE_KEY,
  VALUATION_FEE_KEY,
} from 'common/constants/analyst-dashboards';
import { useStore } from 'common/store';
import { UseStoreValues } from 'common/types/store';
import { useEventBus } from 'context/EventBusContext/EventBusContext';
import { EventType, ValuationNoteChangedPayload } from 'context/EventBusContext/EventBusContext.type';
import { AttioSortDirection, SortCriteria } from 'dashboard409a/common/constants/analyst-dashboard';
import { EngagementManagerOption } from 'dashboard409a/components/ValuationsTable/ValuationsTableRow/ValuationsTableRow.types';
import { ValuationStatus } from 'dashboard409a/pages/Workboard/common/types';
import { getAllAttioValuationsWithBulkData, getAttioUserRecordId } from 'dashboard409a/services/attio';
import { AttioTeamService } from 'dashboard409a/services/attio/AttioTeamService';
import {
  DealGroup,
  SimplifiedAttioValuation,
} from 'dashboard409a/services/attio.types';
import { Note } from 'dashboard409a/services/models.types';
import { compareValuesForSort } from 'dashboard409a/utilities';
import { useLocalStorage } from 'services/hooks/useLocalStorage';
import { getObjectValue } from 'utilities';
import { UseValuationsWithBulkDataProps, UseValuationsWithBulkDataReturn } from './useValuationsWithBulkData.types';

// Constants
// Stable empty-array fallback so `staffUsers` keeps a constant identity while the
// staff query has no data (a `[]` destructuring default would allocate a new array each render).
const EMPTY_STAFF_USERS: never[] = [];
const DEFAULT_ORDER_BY = VALUATION_DATE_KEY;
const DEFAULT_ORDER = DESC;
const USER_RECORD_CACHE_TIME = 60 * 60 * 1000; // 60 minutes
const VALUATIONS_SORT_KEY = 'valuationsSort';
// Generate sorted status options from enum values in workflow order
const SORTED_STATUS_OPTIONS = Object.values(ValuationStatus).map((status, index) => ({
  value: status,
  weight: index + 1,
}));

const getStatusWeight = (status: string): number => {
  const statusOption = SORTED_STATUS_OPTIONS.find(option => option.value === status);
  return statusOption?.weight || 0;
};

// Column type sets for dynamic sorting — keys match record field names
const DATE_COLUMNS = new Set([
  VALUATION_DATE_KEY,
  CREATED_AT_KEY,
  'pay_1_date',
  'pay_2_date',
  'pay_3_date',
  'refund_date',
  'adjustment_date',
  'bad_debt_date',
]);

const CURRENCY_COLUMNS = new Set([
  VALUATION_FEE_KEY,
  'deal_due',
  'net_value',
  'split',
  'payment_1',
  'payment_2',
  'payment_3',
  'refund',
  'due',
  'adjustment',
  'bad_debt',
  'renewal_rate',
]);

// Local sorting function - moved outside component to avoid recreation
const getValue = (record: SimplifiedAttioValuation, sortBy: string): string | number | null => {
  if (sortBy === STATUS_KEY) {
    return getStatusWeight(record.status || '');
  }
  if (sortBy === NOTES_KEY) {
    return record.note?.content || '';
  }
  if (sortBy === DEAL_NAME_KEY) {
    return record.deal || '';
  }

  if (DATE_COLUMNS.has(sortBy)) {
    const dateStr = record[sortBy as keyof SimplifiedAttioValuation] as string | null;
    if (!dateStr) {
      return null;
    }
    const ms = new Date(dateStr).getTime();
    return Number.isNaN(ms) ? null : ms;
  }

  if (CURRENCY_COLUMNS.has(sortBy)) {
    const raw = record[sortBy as keyof SimplifiedAttioValuation] as string | null;
    if (!raw) {
      return null;
    }
    const num = Number(raw.replaceAll(/[^0-9.-]/g, ''));
    return Number.isNaN(num) ? null : num;
  }

  // Default: treat as string field (company_name, engagement_manager, deal, etc.)
  const value = record[sortBy as keyof SimplifiedAttioValuation];
  return (typeof value === 'string' ? value : '') || '';
};

const sortValuationsLocally = (
  valuations: SimplifiedAttioValuation[],
  sortBy: string,
  sortOrder: AttioSortDirection
) => {
  localStorage.setItem(VALUATIONS_SORT_KEY, JSON.stringify({ sortBy, sortOrder }));

  return [...valuations].sort((a, b) => {
    const aValue = getValue(a, sortBy);
    const bValue = getValue(b, sortBy);
    return compareValuesForSort(aValue, bValue, sortOrder);
  });
};

// Multi-criteria sorting function
const sortValuationsWithMultipleCriteria = (
  valuations: SimplifiedAttioValuation[],
  criteria: SortCriteria[]
): SimplifiedAttioValuation[] => {
  if (criteria.length === 0) {
    return [...valuations];
  }

  // Save to localStorage
  localStorage.setItem(VALUATIONS_SORT_KEY, JSON.stringify({ sortCriteria: criteria }));

  return [...valuations].sort((a, b) =>
    criteria.reduce((compareResult, { property, direction }) => {
      if (compareResult !== 0) {
        return compareResult;
      }
      const aValue = getValue(a, property);
      const bValue = getValue(b, property);
      return compareValuesForSort(aValue, bValue, direction);
    }, 0)
  );
};

const getSortedValuations = (
  valuations: SimplifiedAttioValuation[],
  sortCriteria: SortCriteria[] | undefined,
  orderBy: string,
  order: AttioSortDirection
): SimplifiedAttioValuation[] => {
  if (sortCriteria && sortCriteria.length > 0) {
    return sortValuationsWithMultipleCriteria(valuations, sortCriteria);
  }
  const localSort = localStorage.getItem(VALUATIONS_SORT_KEY);
  if (localSort) {
    try {
      const parsed = JSON.parse(localSort);
      if (parsed.sortCriteria?.length > 0) {
        return sortValuationsWithMultipleCriteria(valuations, parsed.sortCriteria);
      }
      if (parsed.sortBy) {
        return sortValuationsLocally(valuations, parsed.sortBy, parsed.sortOrder || DEFAULT_ORDER);
      }
    } catch {
      // fall through to default
    }
  }
  return sortValuationsLocally(valuations, orderBy, order);
};

const sortGroupedRows = (
  groups: DealGroup[],
  sortCriteria: SortCriteria[] | undefined,
  orderBy: string,
  order: AttioSortDirection
): DealGroup[] => {
  if (!groups.length) {
    return [];
  }

  const effectiveCriteria: SortCriteria[] =
    sortCriteria && sortCriteria.length > 0 ? sortCriteria : [{ property: orderBy, direction: order }];

  const groupsWithSortedValuations = groups.map(group => ({
    ...group,
    valuations: getSortedValuations(group.valuations, sortCriteria, orderBy, order),
  }));

  return groupsWithSortedValuations.sort((a, b) =>
    effectiveCriteria.reduce((compareResult, { property, direction }) => {
      if (compareResult !== 0) {
        return compareResult;
      }

      const aFirst = a.valuations[0];
      const bFirst = b.valuations[0];

      if (!aFirst && !bFirst) {
        return 0;
      }
      if (!aFirst) {
        return 1;
      }
      if (!bFirst) {
        return -1;
      }

      if (property === DEAL_NAME_KEY) {
        const aKey = a.deal_name ?? aFirst.deal ?? '';
        const bKey = b.deal_name ?? bFirst.deal ?? '';
        return compareValuesForSort(aKey, bKey, direction);
      }

      const aValue = getValue(aFirst, property);
      const bValue = getValue(bFirst, property);
      return compareValuesForSort(aValue, bValue, direction);
    }, 0)
  );
};

const getNoteRecency = (note: Note | null | undefined): number => {
  if (!note) {
    return 0;
  }
  const updatedAt = note.updated_at ? Date.parse(note.updated_at) : Number.NaN;
  if (!Number.isNaN(updatedAt)) {
    return updatedAt;
  }
  const createdAt = note.created_at ? Date.parse(note.created_at) : Number.NaN;
  if (!Number.isNaN(createdAt)) {
    return createdAt;
  }
  return note.id || 0;
};

const pickLatestNote = (
  existingNote: Note | null | undefined,
  incomingNote: Note | null | undefined
): Note | null => {
  if (!existingNote) {
    return incomingNote ?? null;
  }
  if (!incomingNote) {
    return existingNote;
  }
  return getNoteRecency(incomingNote) >= getNoteRecency(existingNote) ? incomingNote : existingNote;
};

const buildDisplayedValuations = (
  sortedValuations: SimplifiedAttioValuation[],
  prevDisplayedValuations: SimplifiedAttioValuation[]
): SimplifiedAttioValuation[] => {
  const existingDataMap = new Map(prevDisplayedValuations.map(record => [record.record_id, record]));

  return sortedValuations.map(record => {
    const existingData = existingDataMap.get(record.record_id);
    if (existingData) {
      return {
        ...record,
        note: pickLatestNote(existingData.note, record.note),
        is_loading_additional_data: existingData.is_loading_additional_data ?? false,
      };
    }
    return { ...record, is_loading_additional_data: false };
  });
};

export const useValuationsWithBulkData = ({
  filters,
  orderBy = DEFAULT_ORDER_BY,
  order = DEFAULT_ORDER,
  sortCriteria,
  searchParams = { searchParam: '', searchAll: false },
  groupByDeals = false,
}: UseValuationsWithBulkDataProps): UseValuationsWithBulkDataReturn => {
  const queryClient = useQueryClient();
  const { subscribe } = useEventBus();

  // State for data loading
  const [displayedValuations, setDisplayedValuations] = useState<SimplifiedAttioValuation[]>([]);
  const [isInitialLoadComplete, setIsInitialLoadComplete] = useState(false);
  const [selectedUserEmail, setSelectedUserEmail] = useLocalStorage<string>('selectedUserEmail', '');
  const [totalCount, setTotalCount] = useState(0);
  const [allValuations, setAllValuations] = useState<SimplifiedAttioValuation[]>([]);
  const [groupedRows, setGroupedRows] = useState<DealGroup[]>([]);
  const [statusJustChanged, setStatusJustChanged] = useState(false);
  // Get user email from store
  const [{ user }] = useStore() as unknown as UseStoreValues;

  const { email: userEmail = '', is_staff: isStaff } = getObjectValue(user);

  // Get user record ID
  // For superusers: use selectedUserEmail if provided, otherwise fall back to userEmail
  // For non-superusers: always use userEmail
  // Never use empty string - always fall back to userEmail if selectedUserEmail is empty
  const effectiveUserEmail = selectedUserEmail || userEmail;
  const {
    data: userRecordId,
    isLoading: isLoadingUserRecord,
    isFetched: isUserRecordFetched,
  } = useQuery({
    queryKey: [AttioQueryKeys.USER_RECORD_ID, effectiveUserEmail],
    queryFn: () => getAttioUserRecordId(effectiveUserEmail),
    staleTime: USER_RECORD_CACHE_TIME,
    retry: false,
    enabled: !!effectiveUserEmail && effectiveUserEmail.trim() !== '', // Only run if userEmail exists and is not empty
  });

  /// Get users for select list
  const { data: staffUsersData, isLoading: isLoadingStaffUsers } = useQuery({
    queryKey: ['users-list', 'staff'],
    queryFn: () => ApiService.apiUsersStaffList(),
    enabled: isStaff,
    staleTime: USER_RECORD_CACHE_TIME,
    retry: true,
  });
  const staffUsers = staffUsersData ?? EMPTY_STAFF_USERS;

  // Reset all state when the effective user changes (e.g., when selectedUserEmail is cleared)
  useEffect(() => {
    setDisplayedValuations([]);
    setAllValuations([]);
    setGroupedRows([]);
    setIsInitialLoadComplete(!effectiveUserEmail); // If no email, nothing will load — mark complete immediately
    setTotalCount(0);
  }, [effectiveUserEmail]);

  // If user record query completed but found no record, there's nothing to load
  useEffect(() => {
    if (isUserRecordFetched && !userRecordId) {
      setIsInitialLoadComplete(true);
    }
  }, [isUserRecordFetched, userRecordId]);

  // Load all valuations with bulk data in one call
  const {
    data: allValuationsData,
    isLoading: isLoadingAllValuations,
    refetch: refetchValuations,
  } = useQuery({
    queryKey: ['valuations-all', userRecordId, filters, searchParams, groupByDeals],
    queryFn: () =>
      getAllAttioValuationsWithBulkData({
        userRecordId: userRecordId!,
        filters,
        searchParams: searchParams ?? { searchParam: '', searchAll: false },
        groupByDeals,
      }),
    enabled: !!userRecordId,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnMount: 'always',
    staleTime: 10 * 60 * 1000, // 10 minutes cache
    gcTime: 30 * 60 * 1000, // 30 minutes garbage collection
  });

  // Load initial data when all valuations are loaded (now includes bulk data)
  useEffect(() => {
    if (!allValuationsData) {
      return;
    }

    const isGrouped = 'groupedByDeals' in allValuationsData && allValuationsData.groupedByDeals === true;

    if (isGrouped) {
      const grouped = allValuationsData;
      if (grouped.data?.length > 0) {
        setGroupedRows(grouped.data);
        setTotalCount(grouped.totalCount ?? 0);
        setAllValuations([]);
        setDisplayedValuations([]);
        setIsInitialLoadComplete(true);
      } else {
        setGroupedRows([]);
        setAllValuations([]);
        setDisplayedValuations([]);
        setTotalCount(0);
        setIsInitialLoadComplete(true);
      }
      return;
    }

    // Flat response
    setGroupedRows([]);
    if (allValuationsData.data && allValuationsData.data.length > 0) {
      setAllValuations(allValuationsData.data);
      setTotalCount(allValuationsData.totalCount || allValuationsData.data.length);
      if (displayedValuations.length === 0) {
        const enrichedRecords = allValuationsData.data.map((record: SimplifiedAttioValuation) => ({
          ...record,
          is_loading_additional_data: false,
        }));
        setDisplayedValuations(enrichedRecords);
        setIsInitialLoadComplete(true);
      }
    } else {
      setAllValuations([]);
      setDisplayedValuations([]);
      setTotalCount(0);
      setIsInitialLoadComplete(true);
    }
  }, [allValuationsData, displayedValuations.length]);

  // Apply local sorting when data or sorting parameters change
  useEffect(() => {
    if (allValuations.length > 0) {
      // Determine if we should apply delay: only when sorting by status AND status was just changed
      const primarySortProperty = sortCriteria && sortCriteria.length > 0 ? sortCriteria[0].property : orderBy;
      const isSortingByStatus = primarySortProperty === STATUS_KEY;
      const shouldDelay = isSortingByStatus && statusJustChanged;
      const delay = shouldDelay ? 5000 : 0;

      const performSort = () => {
        const sortedValuations = getSortedValuations(allValuations, sortCriteria, orderBy, order);
        setDisplayedValuations(prev => buildDisplayedValuations(sortedValuations, prev));
        if (shouldDelay) {
          setStatusJustChanged(false);
        }
      };

      const timeoutId = setTimeout(performSort, delay);

      return () => {
        clearTimeout(timeoutId);
      };
    }
    return undefined;
  }, [allValuations, orderBy, order, queryClient, statusJustChanged, sortCriteria]);

  const sortedGroupedRows = useMemo(
    () => (groupByDeals ? sortGroupedRows(groupedRows, sortCriteria, orderBy, order) : []),
    [groupByDeals, groupedRows, sortCriteria, orderBy, order]
  );

  // Reset status change flag when sorting changes to something other than status
  useEffect(() => {
    if (orderBy !== STATUS_KEY && statusJustChanged) {
      setStatusJustChanged(false);
    }
  }, [orderBy, statusJustChanged]);

  // Functions to update individual valuations with cache invalidation
  const updateValuation = useCallback(
    (updatedValuation: SimplifiedAttioValuation) => {
      // Update displayed valuations optimistically
      setDisplayedValuations(prevValuations =>
        prevValuations.map(valuation =>
          valuation.record_id === updatedValuation.record_id ? { ...valuation, ...updatedValuation } : valuation
        )
      );

      // Invalidate and refetch the main query to ensure consistency
      queryClient.invalidateQueries({
        queryKey: [AttioQueryKeys.VALUATIONS_ALL, userRecordId, filters, searchParams, groupByDeals],
      });
    },
    [queryClient, userRecordId, filters, searchParams, groupByDeals]
  );

  const updateValuationStatus = useCallback((recordId: string, newStatus: string) => {
    setDisplayedValuations(prevValuations =>
      prevValuations.map(valuation =>
        valuation.record_id === recordId ? { ...valuation, status: newStatus } : valuation
      )
    );
    setAllValuations(prevValuations =>
      prevValuations.map(valuation =>
        valuation.record_id === recordId ? { ...valuation, status: newStatus } : valuation
      )
    );
    setGroupedRows(prev =>
      prev.map(group => ({
        ...group,
        valuations: group.valuations.map(v => (v.record_id === recordId ? { ...v, status: newStatus } : v)),
      }))
    );
    setStatusJustChanged(true);
  }, []);

  const updateValuationNotes = useCallback(
    (recordId: string, newNotes: string, noteId?: number) => {
      // Create updated note object
      const updatedNote: Note = {
        id: noteId ?? 0,
        content: newNotes,
        author_id: 0,
        author_email: '',
        author_name: '',
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };

      // Note: Notes are now included in the main valuations query, so we invalidate that instead

      setDisplayedValuations(prevValuations =>
        prevValuations.map(valuation =>
          valuation.record_id === recordId ? { ...valuation, note: updatedNote } : valuation
        )
      );
      setAllValuations(prevValuations =>
        prevValuations.map(valuation =>
          valuation.record_id === recordId ? { ...valuation, note: updatedNote } : valuation
        )
      );
      setGroupedRows(prev =>
        prev.map(group => ({
          ...group,
          valuations: group.valuations.map(v => (v.record_id === recordId ? { ...v, note: updatedNote } : v)),
        }))
      );

      queryClient.invalidateQueries({
        queryKey: [AttioQueryKeys.VALUATIONS_ALL, userRecordId, filters, searchParams, groupByDeals],
      });

      // Invalidate additional data cache for this specific record
      queryClient.invalidateQueries({
        queryKey: [AttioQueryKeys.ADDITIONAL_DATA],
        predicate: query => {
          const { queryKey } = query;
          if (Array.isArray(queryKey) && queryKey[0] === AttioQueryKeys.ADDITIONAL_DATA) {
            const recordIds = queryKey[1]?.toString().split(',') || [];
            return recordIds.includes(recordId);
          }
          return false;
        },
      });
    },
    [queryClient, userRecordId, filters, searchParams, groupByDeals]
  );

  // Sync table when a note is added or deleted from the modal
  useEffect(() => {
    const unsubscribe = subscribe(
      EventType.VALUATION_NOTE_CHANGED,
      ({ valuationId, latestNote }: ValuationNoteChangedPayload) => {
        const updatedNote: Note | null = latestNote
          ? {
            id: latestNote.id,
            content: latestNote.content,
            author_id: 0,
            author_email: '',
            author_name: '',
            created_at: '',
            updated_at: '',
          }
          : null;

        setDisplayedValuations(prev => prev.map(v => (v.record_id === valuationId ? { ...v, note: updatedNote } : v)));
        setAllValuations(prev => prev.map(v => (v.record_id === valuationId ? { ...v, note: updatedNote } : v)));
        setGroupedRows(prev =>
          prev.map(group => ({
            ...group,
            valuations: group.valuations.map(v => (v.record_id === valuationId ? { ...v, note: updatedNote } : v)),
          }))
        );
      }
    );
    return unsubscribe;
  }, [subscribe]);

  const updateValuationEngagementManager = useCallback(
    async (recordId: string, engagementManagerOption: EngagementManagerOption) => {
      const { targetRecordId, name, imageSrc } = engagementManagerOption;

      // Only fetch from API when a real manager is being set
      if (targetRecordId) {
        const teamService = new AttioTeamService();
        const managerData = await teamService.getTeamMember(targetRecordId);
        queryClient.setQueryData(
          [AttioQueryKeys.ENGAGEMENT_MANAGER, targetRecordId],
          { name: managerData.name, imageSrc: managerData.imageSrc }
        );
      }

      const updatedFields = {
        engagement_manager: name,
        engagement_manager_image_src: imageSrc,
        engagement_manager_id: targetRecordId,
      };

      setAllValuations(prev =>
        prev.map(v => v.record_id === recordId ? { ...v, ...updatedFields } : v)
      );
      setDisplayedValuations(prev =>
        prev.map(v => v.record_id === recordId ? { ...v, ...updatedFields } : v)
      );
      setGroupedRows(prev =>
        prev.map(group => ({
          ...group,
          valuations: group.valuations.map(v =>
            v.record_id === recordId ? { ...v, ...updatedFields } : v
          ),
        }))
      );
    },
    [queryClient]
  );

  const updateValuationOtherConsultants = useCallback(
    async (recordId: string, options: { targetRecordId: string; name: string; imageSrc: string }[]) => {
      const updatedFields = {
        other_consultant: options.map(o => o.name) as unknown as string,
        other_consultant_id: options.map(o => o.targetRecordId) as unknown as string,
        other_consultant_image_src: options.map(o => o.imageSrc) as unknown as string,
      };

      setDisplayedValuations(prev =>
        prev.map(v => v.record_id === recordId ? { ...v, ...updatedFields } : v)
      );
      setGroupedRows(prev =>
        prev.map(group => ({
          ...group,
          valuations: group.valuations.map(v =>
            v.record_id === recordId ? { ...v, ...updatedFields } : v
          ),
        }))
      );
    },
    []
  );

  const refetch = useCallback(() => {
    if (userRecordId) {
      refetchValuations();
    }
  }, [userRecordId, refetchValuations]);

  return {
    // Data
    rows: displayedValuations,
    groupedRows: groupByDeals ? sortedGroupedRows : [],
    totalCount,
    staffUsers,
    isLoadingStaffUsers,
    setSelectedUserEmail,
    selectedUserEmail,

    // Loading states
    isLoading: !!effectiveUserEmail && (isLoadingUserRecord || isLoadingAllValuations || !isInitialLoadComplete),
    isError: false,
    error: null,

    // Update functions
    updateValuation,
    updateValuationStatus,
    updateValuationNotes,
    updateValuationEngagementManager,
    updateValuationOtherConsultants,
    refetch,
  };
};
